import { faker } from '@faker-js/faker';
import { createMock, DeepMocked } from '@golevelup/ts-jest';

import { Authority, User } from '@project44/ui-utils';
import {
  DtoBase,
  Enums,
  GenerateTestModels,
  SharedModels,
  TenantEnums,
  TenantModels,
  UIDtoFactory,
  UserEnums,
  UserModels,
} from '@project44-models/iam-gateway';
import { BooleanAsStringEnum } from '@project44-models/iam-gateway/dist/enums';

import {
  IUnifiedRolesDomain,
  UnifiedRolesDomainAbstractions,
} from '../domains/UnifiedRoles';
import { UnifiedRolesMapper } from '../domains/UnifiedRoles/domain/mapper/UnifiedRoles.mapper';
import {
  IUserCreationForm,
  TAssignedUserTenantAgnosticRolesState,
  TAssignedUserTenantSpecificRolesState,
  TUIUserTenantAgnosticRoles,
  TUIUserTenantSpecificRoles,
} from '../domains/User/domain/interfaces';
import { ResourceIdentifierModel } from '../shared/abstract/domain/ResourceIdentifierModel';
import { ResourceIdentifier } from '../shared/core/domain/ResourceIdentifier';

export const p44EmailExample = faker.internet.email(
  `iam-gateway-${faker.name.firstName()}`,
  `${faker.name.lastName()}.${faker.datatype.bigInt().toString()}`,
  'p-44.com',
);

export class MockDataFactory {
  public static p44EmailExample = p44EmailExample;
  public static readonly createDeepMockedUnifiedRolesEditorPolicy =
    (): DeepMocked<UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy> => {
      return createMock<UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy>();
    };

  public static createUserCreationForm = (
    payload?: Partial<IUserCreationForm>,
  ): IUserCreationForm => {
    return {
      ...payload,
      familyName: faker.name.lastName(),
      givenName: faker.name.firstName(),
      username: p44EmailExample,
      password: 'IAM team 2020',
      phoneNumber: faker.phone.number().toString(),
      userType: UserEnums.UserTypeEnum.USER,
      isAdmin: BooleanAsStringEnum.FALSE,
    };
  };
  public static readonly createUser = (payload?: Partial<User>): User => {
    return new User({
      authorities: [
        {
          authority: Authority.ROLE_P44ADMIN,
        },
      ],
      companyName: faker.company.name(),
      customerProperties: {},
      firstName: faker.name.firstName(),
      id: faker.datatype.number(),
      locale: faker.locale,
      lastName: faker.name.lastName(),
      tenantCarrierNetworkRoles: [TenantEnums.CarrierNetworkRolesEnum.CARRIER],
      tenantId: faker.datatype.number(),
      tenantUuid: faker.datatype.uuid(),
      username: p44EmailExample,
      ...(payload && {
        ...payload,
      }),
    });
  };

  public static readonly createAuthoritiesCollection = (
    permissions?: Array<string>,
  ): User['authorities'] => {
    return permissions === undefined
      ? []
      : permissions.map((authority) => {
          return {
            authority: authority as unknown as Authority,
          };
        });
  };

  public static readonly createUserResourceIdentifier = (
    payload?: Partial<User>,
  ): ResourceIdentifierModel => {
    const userModel = MockDataFactory.createUser(payload);

    return ResourceIdentifier.create(userModel.username, {
      tenantUuid: userModel.tenantUuid,
    });
  };

  public static readonly createSystemAdminUser = (): User => {
    return MockDataFactory.createUser({
      authorities: [
        {
          authority: Authority.ROLE_P44ADMIN,
        },
      ],
    });
  };

  public static readonly createNonSystemAdminUser = (): User => {
    return MockDataFactory.createUser({
      authorities: [
        {
          authority: Authority.TL_VISIBILITY_UI,
        },
      ],
    });
  };

  public static readonly createTenantQueryViewDto =
    (): DtoBase<TenantModels.TenantQueryViewModel> => {
      return UIDtoFactory.create(
        Enums.UiDtoFactoryEnum.TENANT_QUERY,
        GenerateTestModels.createTenantQueryViewModel(),
      );
    };

  public static readonly createTenantQueryResultsDto = (
    partialTenantQueryResults?: Partial<TenantModels.TenantQueryResultsViewModel>,
  ): DtoBase<TenantModels.TenantQueryResultsViewModel> => {
    return UIDtoFactory.create(
      Enums.UiDtoFactoryEnum.TENANT_QUERY_RESULTS,
      GenerateTestModels.createTenantQueryResultsViewModel(
        partialTenantQueryResults,
      ),
    );
  };

  public static readonly createTenantDto = (
    partialTenant?: Partial<TenantModels.TenantViewModel>,
  ): DtoBase<TenantModels.TenantViewModel> => {
    return UIDtoFactory.create(
      Enums.UiDtoFactoryEnum.TENANT,
      GenerateTestModels.createTenantViewModel(partialTenant),
    );
  };

  public static readonly createUnifiedRole = (
    partialUnifiedRole?: Partial<SharedModels.UnifiedRoleViewModel>,
  ): SharedModels.UnifiedRoleViewModel => {
    return UIDtoFactory.create(Enums.UiDtoFactoryEnum.UNIFIED_ROLE_COLLECTION, {
      roles: [
        {
          id: partialUnifiedRole?.id || faker.datatype.uuid(),
          name: partialUnifiedRole?.name || faker.datatype.uuid(),
          category: partialUnifiedRole?.category || faker.datatype.uuid(),
          description:
            partialUnifiedRole?.description ||
            faker.commerce.productDescription(),
        },
      ],
    }).toSerializedModel().roles[0];
  };

  public static readonly createUserDto = (
    partialUser?: Partial<UserModels.UserViewModel>,
  ): DtoBase<UserModels.UserViewModel> => {
    return UIDtoFactory.create(
      Enums.UiDtoFactoryEnum.USER,
      GenerateTestModels.createUserViewModel(partialUser),
    );
  };

  public static readonly createUserDetailsDto = (
    partialUserCreation?: Partial<UserModels.UserCreationViewModel>,
  ): DtoBase<UserModels.UserCreationViewModel> => {
    return UIDtoFactory.create(
      Enums.UiDtoFactoryEnum.USER_CREATION,
      GenerateTestModels.createUserCreationViewModel(partialUserCreation),
    );
  };

  public static readonly createUnifiedRoleCollectionDto = (
    partialUnifiedRoleCollectionViewModel?: Partial<SharedModels.UnifiedRoleCollectionViewModel>,
  ): DtoBase<SharedModels.UnifiedRoleCollectionViewModel> => {
    return UIDtoFactory.create(
      Enums.UiDtoFactoryEnum.UNIFIED_ROLE_COLLECTION,
      GenerateTestModels.createUnifiedRoleCollectionViewModel(
        partialUnifiedRoleCollectionViewModel,
      ),
    );
  };

  public static createUIUserTenantSpecificUnifiedRolesAggregate = (
    partialPayload?: Partial<TUIUserTenantSpecificRoles>,
  ): TUIUserTenantSpecificRoles => {
    return createMock<TUIUserTenantSpecificRoles>({
      isTenantSpecificRolesApiRequestInProgress: false,
      userTenantSpecificRolesEditor:
        createMock<UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy>({
          getAllAvailableUnifiedRoles(): IUnifiedRolesDomain.TUnifiedRoleCollectionDto {
            return MockDataFactory.createUnifiedRoleCollectionDto();
          },
        }),
      ...(partialPayload || {}),
    });
  };

  public static createUIUserTenantAgnosticUnifiedRolesAggregate = (
    partialPayload?: Partial<TUIUserTenantSpecificRoles>,
  ): TUIUserTenantAgnosticRoles => {
    return createMock<TUIUserTenantAgnosticRoles>({
      isTenantAgnosticRolesApiRequestInProgress: false,
      userTenantAgnosticRolesEditor:
        createMock<UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy>({
          getAllAvailableUnifiedRoles(): IUnifiedRolesDomain.TUnifiedRoleCollectionDto {
            return MockDataFactory.createUnifiedRoleCollectionDto();
          },
        }),
      ...(partialPayload || {}),
    });
  };
  public static readonly createSystemAdminWithReadTenantSpecificRoles =
    (): User => {
      return new User(
        MockDataFactory.createUser({
          authorities: [
            {
              authority: <Authority>(
                'system-admin:read:tenant-specific-user-role-assignments'
              ),
            },
          ],
        }),
      );
    };

  public static readonly createAssignedUserTenantSpecificRolesState =
    (payload?: {
      unifiedRoles?: SharedModels.UnifiedRoleCollectionViewModel;
    }): TAssignedUserTenantSpecificRolesState => {
      const mockUpdateUnifiedRoles = jest.fn();

      const unifiedRoles: SharedModels.UnifiedRoleCollectionViewModel =
        payload?.unifiedRoles ||
        GenerateTestModels.createUnifiedRoleCollectionViewModel();

      return [
        new Map(
          unifiedRoles.roles.map((unifiedRole) => {
            return [unifiedRole.id, unifiedRole];
          }),
        ),
        mockUpdateUnifiedRoles,
      ];
    };

  public static readonly createAssignedUserTenantAgnosticRolesState =
    (payload?: {
      unifiedRoles?: SharedModels.UnifiedRoleCollectionViewModel;
    }): TAssignedUserTenantAgnosticRolesState => {
      const mockUpdateUnifiedRoles = jest.fn();

      const unifiedRoles: SharedModels.UnifiedRoleCollectionViewModel =
        payload?.unifiedRoles ||
        GenerateTestModels.createUnifiedRoleCollectionViewModel();

      return [
        new Map(
          unifiedRoles.roles.map((unifiedRole) => {
            return [unifiedRole.id, unifiedRole];
          }),
        ),
        mockUpdateUnifiedRoles,
      ];
    };

  public static readonly composeNMCLegacyTenantAdminRole =
    (): SharedModels.UnifiedRoleViewModel => {
      return UnifiedRolesMapper.assertNMCLegacyTenantAdminRole({
        id: faker.datatype.uuid(),
        name: 'administrator',
        category: faker.datatype.uuid(),
        description: faker.commerce.productDescription(),
      });
    };

  public static readonly composeTenantAdminRole =
    (): SharedModels.UnifiedRoleViewModel => {
      return UnifiedRolesMapper.assertTenantAdminRole({
        id: '7',
        name: faker.datatype.uuid(),
        category: faker.datatype.uuid(),
        description: faker.commerce.productDescription(),
      });
    };

  public static readonly composeNMCLegacyReadOnlyRole =
    (): SharedModels.UnifiedRoleViewModel => {
      return UnifiedRolesMapper.assertNMCLegacyReadOnlyRole({
        id: faker.datatype.uuid(),
        name: 'read-only',
        category: faker.datatype.uuid(),
        description: faker.commerce.productDescription(),
      });
    };

  public static readonly composeUnifiedRolesEditorPolicy =
    (): DeepMocked<UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy> => {
      return createMock<UnifiedRolesDomainAbstractions.UnifiedRolesEditorPolicy>();
    };

  public static readonly createSystemAdminWithReadAndWriteTenantSpecificRoles =
    (): User => {
      return new User(
        MockDataFactory.createUser({
          authorities: [
            {
              authority: <Authority>(
                'system-admin:read:tenant-specific-user-role-assignments'
              ),
            },
            {
              authority: <Authority>(
                'system-admin:update:tenant-specific-user-role-assignments'
              ),
            },
            {
              authority: <Authority>'SAVE_USER_ROLES',
            },
          ],
        }),
      );
    };
}
